'use client';

import { useRef, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { type FragmentType, getFragmentData } from '@packages/gql/generated/shopping';
import { AplusFragmentFragmentDoc } from '@packages/gql/generated/shopping/AplusFragmentFragmentDoc';
import { ToggleServiceProductDocument } from '@packages/gql/generated/shopping/ToggleServiceProductDocument';
import { useAuthMutation } from '@packages/shared/src/hooks/useAuthMutation/useAuthMutation';
import type { APlusData } from '@packages/modules/src/APlus/types';
import { getInitialAplusData } from '@packages/modules/src/APlus/helper/getInitialAplusData';
import { AplusAccordion } from './AplusAccordion.client';
import { transformBasketAplusToItems } from './transformBasketAplusToItems';

/* GraphQL */ `
  fragment AplusFragment on BasketAPlus {
    clusters {
      label
      services {
        serviceType
        serviceIcon
        uuid
        name
        price {
          value
          currency
        }
        included
        layerLink
        selected
        serviceCount {
          current
          maximum
        }
      }
    }
  }
`;

/* GraphQL */ `
  mutation ToggleServiceProduct(
    $locale: String!
    $lineItemId: String!
    $serviceUuid: String!
    $count: Int
  ) {
    toggleServiceProduct(
      locale: $locale
      lineItemId: $lineItemId
      serviceUuid: $serviceUuid
      count: $count
    ) {
      ...BasketFragment
    }
  }
`;

type BasketAplusProps = {
  aplusData: FragmentType<typeof AplusFragmentFragmentDoc>;
  lineItemId: string;
};

export const BasketAplus = ({ aplusData, lineItemId }: BasketAplusProps) => {
  const { locale } = useIntl();
  const [state, toggleServiceProduct] = useAuthMutation(ToggleServiceProductDocument);
  const countTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const fragmentData = getFragmentData(AplusFragmentFragmentDoc, aplusData);
  const { items, currency } = useMemo(
    () => transformBasketAplusToItems(fragmentData.clusters),
    [fragmentData.clusters],
  );

  const prevDataRef = useRef<APlusData>(getInitialAplusData(items));

  const onChangeHandler = (newData: APlusData) => {
    const prev = prevDataRef.current;
    for (const [uuid, newEntry] of newData.entries()) {
      const prevEntry = prev.get(uuid);
      if (prevEntry) {
        if (prevEntry.selected !== newEntry.selected) {
          if (!state.fetching) {
            toggleServiceProduct({ locale, lineItemId, serviceUuid: uuid });
          }
          break;
        }

        if (prevEntry.value !== newEntry.value && newEntry.value !== undefined) {
          if (countTimeoutRef.current) clearTimeout(countTimeoutRef.current);
          const count = newEntry.value;
          countTimeoutRef.current = setTimeout(() => {
            toggleServiceProduct({ locale, lineItemId, serviceUuid: uuid, count });
          }, 1500);
          break;
        }
      }
    }
    prevDataRef.current = newData;
  };

  return <AplusAccordion items={items} currency={currency} onChangeHandler={onChangeHandler} />;
};
